// HU-11 · Bandeja del Comité y apertura del caso. F-03, Sprint 2 (rev. 16, 23 criterios).
//
// LA BANDEJA PRIORIZA, NO ESCONDE (RN-28). El servidor devuelve los cinco bloques YA
// clasificados y ordenados —Observados, Vencidos, Por vencer, En plazo, Graves sin
// ampliar— y esta pantalla los pinta tal cual. La clasificación no se rehace aquí: el
// reloj interno se cuenta en horas hábiles en la zona de cada país (RN-19), y eso lo sabe
// el backend, que tiene el calendario. Un front que recalculara tendría dos verdades.
//
// SIN ÁMBITO POR PAÍS (RN-40, ILENDER 10-sep-2026): el Comité resuelve para todos los
// países; el filtro de país es un filtro, no un permiso.

import { pedir } from './cliente';

export type EstadoDelPlazo = 'en_plazo' | 'por_vencer' | 'vencido' | 'detenido';
export type ClaveDeBloque = 'observados' | 'vencidos' | 'por_vencer' | 'en_plazo' | 'graves_sin_ampliar';

export type FilaDeBandeja = {
  caso_id: string;
  numero: string | null;
  pais: string;
  nombre_pais: string;
  producto: string | null;
  fecha_recepcion: string;
  estado_del_caso: string;
  /** C7: `null` = el Comité no ha decidido; la columna va VACÍA, no «No grave». */
  gravedad: 'grave' | 'no_grave' | null;
  estado_del_plazo: EstadoDelPlazo;
  /** C8: el texto que acompaña al ícono («Plazo interno vencido»). */
  texto_del_plazo: string;
  vencimiento_interno: string | null;
  plazo_fiable: boolean;
  accion_pendiente: string;
  /** C5: el motivo por el que el aprobador devolvió el informe. */
  motivo_de_observacion: string | null;
};

export type BloqueDeBandeja = {
  clave: ClaveDeBloque;
  titulo: string;
  orden: number;
  cuenta: number;
  filas: FilaDeBandeja[];
};

export type BandejaDelComite = {
  bloques: BloqueDeBandeja[];
  total: number;
  filtrado_por: string | null;
  mensaje_de_vacia: string | null;
  paises: { codigo: string; nombre: string }[];
  plazo_interno_horas_habiles: number;
  consultada_en: string;
  /**
   * Cuántos candidatos quedaron FUERA por el tope de la consulta (HU-31). Cero en la
   * operación normal. Distinto de cero significa que la bandeja no está completa, y
   * entonces hay que decirlo: un recorte callado deja casos recientes invisibles.
   */
  candidatos_fuera_del_tope?: number;
};

export type FiltrosDeBandeja = {
  pais?: string;
  estado_plazo?: EstadoDelPlazo | '';
  gravedad?: 'grave' | 'no_grave' | '';
  producto?: string;
  desde?: string;
  hasta?: string;
  numero?: string;
};

/** C20: un caso de CUALQUIER estado, encontrado por número desde «Ver todos los casos». */
export type CasoEncontrado = {
  caso_id: string;
  numero: string | null;
  pais: string;
  nombre_pais: string;
  producto: string | null;
  fecha_recepcion: string;
  estado_del_caso: string;
  gravedad: 'grave' | 'no_grave' | null;
};

export function consultarBandeja(filtros: FiltrosDeBandeja = {}) {
  const parametros = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor) parametros.set(clave, String(valor).trim());
  }
  const consulta = parametros.toString();
  return pedir<BandejaDelComite>(consulta ? `/evaluacion/bandeja?${consulta}` : '/evaluacion/bandeja');
}

/**
 * C20: lo que devuelve «Ver todos los casos». El servidor manda hasta `tope` coincidencias
 * (las de recepción más reciente) y `hay_mas` cuando quedaron otras fuera: la pantalla lo
 * dice en vez de cortar en silencio.
 */
export type BusquedaEnTodosLosCasos = {
  casos: CasoEncontrado[];
  hay_mas: boolean;
  tope: number;
};

export function buscarEnTodosLosCasos(numero: string) {
  return pedir<BusquedaEnTodosLosCasos>(`/evaluacion/bandeja/todos-los-casos?numero=${encodeURIComponent(numero.trim())}`);
}
